import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TailSpin } from 'react-loader-spinner';
import SftGlobalData from '../library/SftGlobalData';
import Colors from '../library/Colors';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ReportPreviewDialog from '../components/reports/ReportPreviewDialog';

/**
 * UploadsScreen — MyFasMan Mobile › Uploads (design artifact §4).
 *
 * Client files the mobile POS prints/displays, managed per scope:
 * '0000' global · 'SS00' store · 'SSPP' register (the kasvar location codes).
 * Tabs: ticket logo, invoice logo, document layouts (client .fr3 forms +
 * per document kind the kasvar assignment grid: invoice = ReportFactuur1,
 * webprint = WebshopPickingListReportid / ReportDeliveryNote, with the
 * layouts of an assigned report id and a preview), label layouts (+
 * designer entry), lock screen.
 *
 * SoftTouch users only — every rank incl. supp; the server enforces the same
 * gate per action.
 */
const UploadsScreen = (props) => {
    const navigate = useNavigate();

    // The tab survives a round-trip to the designer (back used to always
    // land on the first tab).
    const [tab, setTabState] = useState(() => {
        const stored = parseInt(window.sessionStorage.getItem('upl_tab') || '0', 10);
        return isNaN(stored) ? 0 : Math.min(4, Math.max(0, stored));
    });
    const setTab = (value) => {
        window.sessionStorage.setItem('upl_tab', String(value));
        setTabState(value);
    };
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [assets, setAssets] = useState(null);
    const [forms, setForms] = useState(null);

    // Upload dialog state (images).
    const [dlgOpen, setDlgOpen] = useState(false);
    const [dlgKind, setDlgKind] = useState('ticket_logo');
    const [dlgScopeType, setDlgScopeType] = useState('global'); // global|store|register
    const [dlgStore, setDlgStore] = useState('');
    const [dlgRegister, setDlgRegister] = useState('');
    const [dlgImage, setDlgImage] = useState('');

    // Assignment dialog state (whitelisted document kasvar).
    const [asgOpen, setAsgOpen] = useState(false);
    const [asgKasvar, setAsgKasvar] = useState('ReportFactuur1');
    // Document preview (report id + document id input).
    const [preview, setPreview] = useState(null); // {repid, title, kind, storeId, posId}
    const [asgScopeType, setAsgScopeType] = useState('global');
    const [asgStore, setAsgStore] = useState('');
    const [asgRegister, setAsgRegister] = useState('');
    const [asgValue, setAsgValue] = useState('');

    const imgFileRef = useRef(null);
    const fr3FormRef = useRef(null);
    const fr3LabelRef = useRef(null);

    const isSft = props.isSft === true;
    const t = props.t || ((k, d) => d || k);
    // Sidebar-aware content class (same contract as the other screens:
    // .expanded clears the drawer, .not_expanded is the mobile layout).
    const containerClass = (props.screenWidth < 600 ? 'not_expanded ' : 'expanded ')
        + 'container_content _80perc ' + props.theme + ' container_content_' + props.theme;
    const isDark = props.theme === 'dark';
    const panelBorder = isDark ? Colors.dark_accent_darker : Colors.light_accent_darker;
    const inkFaint = isDark ? Colors.dark_text_accent : '#6B7280';

    const notify = (text, ok) => {
        if (props.notify) props.notify(text, 5000, 'top-right', props.theme, ok ? 1 : 2);
    };

    // ------------------------------------------------------------------
    //  DATA
    // ------------------------------------------------------------------

    const reload = useCallback(() => {
        if (props.api === '' || !isSft) return;
        SftGlobalData.post('uploads&action=listAssets',
            { key: props.api, sftUserId: props.sftUserId }).then((response) => {
            if (response?.success) setAssets(response.data);
            setLoading(false);
        });
        SftGlobalData.post('uploads&action=listDocumentLayouts',
            { key: props.api, sftUserId: props.sftUserId }).then((response) => {
            if (response?.success) setForms(response.data);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.api, isSft]);

    useEffect(() => { reload(); }, [reload]);

    const dialogScope = (scopeType, store, register) => {
        if (scopeType === 'store') return store !== '' ? store + '00' : '';
        if (scopeType === 'register') return register;
        return '0000';
    };

    const scopeLabel = (scope) => {
        if (scope === '0000') return t('upl_global', 'Global');
        if (scope.substring(2) === '00') {
            const store = (assets?.stores || []).find((entry) => entry.code === scope.substring(0, 2));
            return t('upl_store', 'Store') + ' ' + scope.substring(0, 2) + (store ? ' — ' + store.oms : '');
        }
        const register = (assets?.registers || []).find((entry) => entry.code === scope);
        return t('upl_register', 'Register') + ' ' + scope + (register ? ' — ' + register.oms : '');
    };

    // ------------------------------------------------------------------
    //  ACTIONS
    // ------------------------------------------------------------------

    const openUploadDialog = (kind) => {
        setDlgKind(kind);
        setDlgScopeType('global');
        setDlgStore('');
        setDlgRegister('');
        setDlgImage('');
        setDlgOpen(true);
    };

    const submitUpload = () => {
        const scope = dialogScope(dlgScopeType, dlgStore, dlgRegister);
        if (scope === '' || dlgImage === '') return;
        setBusy(true);
        SftGlobalData.post('uploads&action=uploadAsset', {
            key: props.api, sftUserId: props.sftUserId,
            kind: dlgKind, scope, image: dlgImage,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setDlgOpen(false);
                notify(t('upl_uploaded', 'Uploaded'), true);
                reload();
            } else {
                notify(response?.message || 'Upload failed', false);
            }
        });
    };

    const deleteAsset = (kind, scope) => {
        if (!window.confirm(t('upl_confirm_delete', 'Delete this file?'))) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=deleteAsset', {
            key: props.api, sftUserId: props.sftUserId, kind, scope,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Delete failed', false);
        });
    };

    /** Shared .fr3 uploader: reads bytes, base64-encodes (1252-safe transport). */
    const uploadFr3 = (e, action) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!/\.fr3$/i.test(file.name)) {
            notify(t('upl_fr3_only', 'Only .fr3 files — FastReport 2 (.frf) forms only render on Windows MySales'), false);
            return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
            const bytes = new Uint8Array(ev.target.result);
            let binary = '';
            for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
            setBusy(true);
            SftGlobalData.post('uploads&action=' + action, {
                key: props.api, sftUserId: props.sftUserId,
                name: file.name, content: btoa(binary),
            }).then((response) => {
                setBusy(false);
                if (response?.success) {
                    notify(t('upl_uploaded', 'Uploaded'), true);
                    reload();
                } else {
                    notify(response?.message || 'Upload failed', false);
                }
            });
        };
        reader.readAsArrayBuffer(file);
    };

    const deleteFr3 = (action, name) => {
        if (!window.confirm(t('upl_confirm_delete', 'Delete this file?') + ' (' + name + ')')) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=' + action, {
            key: props.api, sftUserId: props.sftUserId, name,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Delete failed', false);
        });
    };

    /**
     * Make a report layout editable: the server copies it from the shared
     * reports2 store into the client's custom dir (a custom copy — the
     * shared store is never written), after which it edits like any form.
     */
    const importReportForm = (name) => {
        if (!window.confirm(t('upl_confirm_fetch',
            'Copy this layout into the client\'s custom reports so it can be edited?') + ' (' + name + ')')) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=importReportForm', {
            key: props.api, sftUserId: props.sftUserId, name,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Fetch failed', false);
        });
    };

    /**
     * Standard reports are never edited here: MyFashionManager "Rapport
     * kopiëren" makes a CUSTOM copy (cm_ layouts in the client dir), after
     * which the assignment can be moved to the new repid at the same scope.
     */
    const copyReportAsCustom = (repid, currentTitle, kasvar, scope) => {
        const title = window.prompt(t('rla_copy_title', 'Title of the new custom report (empty = same title):'), currentTitle || '');
        if (title === null) return;
        setBusy(true);
        SftGlobalData.post('uploads&action=copyReport', {
            key: props.api, sftUserId: props.sftUserId, repid,
            title: title.trim() === (currentTitle || '') ? '' : title.trim(),
        }).then((response) => {
            setBusy(false);
            if (!response?.success) {
                notify(response?.message || 'Copy failed', false);
                return;
            }
            const result = response.data || {};
            const problems = [...(result.missing_files || []), ...(result.failed_files || [])];
            notify(t('rla_copied', 'Custom report created:') + ' ' + result.repid
                + (problems.length ? ' — ' + t('rla_copy_missing', 'some layout files could not be copied:') + ' ' + problems.join(', ') : ''),
                problems.length === 0);
            if (kasvar && scope && window.confirm(t('upl_copy_reassign',
                'Assign the new custom report to the same scope?') + ' (' + kasvar + ' · ' + scope + ' → ' + result.repid + ')')) {
                setBusy(true);
                SftGlobalData.post('uploads&action=assignDocumentLayout', {
                    key: props.api, sftUserId: props.sftUserId, kasvar, scope, value: String(result.repid),
                }).then((assigned) => {
                    setBusy(false);
                    if (!assigned?.success) notify(assigned?.message || 'Assignment failed', false);
                    reload();
                });
            } else {
                reload();
            }
        });
    };

    const submitAssignment = () => {
        const scope = dialogScope(asgScopeType, asgStore, asgRegister);
        if (scope === '') return;
        setBusy(true);
        SftGlobalData.post('uploads&action=assignDocumentLayout', {
            key: props.api, sftUserId: props.sftUserId, kasvar: asgKasvar, scope, value: asgValue,
        }).then((response) => {
            setBusy(false);
            if (response?.success) {
                setAsgOpen(false);
                notify(t('upl_assigned', 'Assignment saved'), true);
                reload();
            } else {
                notify(response?.message || 'Assignment failed', false);
            }
        });
    };

    const clearAssignment = (kasvar, scope) => {
        setBusy(true);
        SftGlobalData.post('uploads&action=clearDocumentAssignment', {
            key: props.api, sftUserId: props.sftUserId, kasvar, scope,
        }).then((response) => {
            setBusy(false);
            if (response?.success) reload();
            else notify(response?.message || 'Failed', false);
        });
    };

    const pickImage = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (ev) => setDlgImage(ev.target.result);
        reader.readAsDataURL(file);
    };

    // ------------------------------------------------------------------
    //  RENDER PIECES
    // ------------------------------------------------------------------

    const fileRow = (children) => (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap',
            border: '1px solid ' + panelBorder, borderRadius: 2, p: 1.2, mb: 1,
            backgroundColor: isDark ? Colors.dark_accent : '#FFFFFF',
        }}>
            {children}
        </Box>
    );

    const smallBtn = (label, onClick, danger, solid) => (
        <Button size="small" variant={solid ? 'contained' : 'outlined'} disabled={busy} onClick={onClick}
            sx={{
                fontSize: 12.5, textTransform: 'none', py: 0.35, px: 1.4, minWidth: 0,
                borderColor: danger ? Colors.def_red : Colors.sft_blue,
                color: solid ? '#fff' : (danger ? Colors.def_red : Colors.sft_blue),
                backgroundColor: solid ? Colors.sft_blue : undefined,
            }}>
            {label}
        </Button>
    );

    const scopeChip = (scope) => (
        <Chip size="small" label={scopeLabel(scope)}
            sx={{ fontSize: 12, fontWeight: 700, backgroundColor: '#E1EFF8', color: '#075985' }} />
    );

    /** One image kind tab: existing files + inheriting stores + upload. */
    const imageKindPane = (kind, intro) => {
        const files = (assets?.kinds && assets.kinds[kind]) ? assets.kinds[kind] : [];
        const coveredStores = {};
        files.forEach((file) => {
            if (file.scope !== '0000' && file.scope.substring(2) === '00') coveredStores[file.scope.substring(0, 2)] = true;
        });
        const hasGlobal = files.some((file) => file.scope === '0000');

        return (
            <div>
                {intro && (
                    <Box sx={{ mb: 1.2, p: 1.2, borderRadius: 1.5, backgroundColor: '#FDF6E3', border: '1px solid #F0E1B8', color: '#7A5D00', fontSize: 13.5 }}>
                        {intro}
                    </Box>
                )}
                {files.map((file) => (
                    <React.Fragment key={kind + file.scope}>
                        {fileRow(
                            <React.Fragment>
                                <img src={file.url + '?v=' + Date.now()} alt={file.scope}
                                    style={{ width: 84, height: 34, objectFit: 'contain', border: '1px solid ' + panelBorder, borderRadius: 4, background: '#fff' }} />
                                {scopeChip(file.scope)}
                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{file.file.split('/').pop()}</Typography>
                                <Box sx={{ flex: 1 }} />
                                {smallBtn(t('upl_delete', 'Remove'), () => deleteAsset(kind, file.scope), true)}
                            </React.Fragment>
                        )}
                    </React.Fragment>
                ))}
                {!hasGlobal && files.length === 0 && (
                    <Typography sx={{ fontSize: 13.5, color: inkFaint, mb: 1 }}>
                        {t('upl_no_files', 'No files yet — the app falls back to the current (legacy) behaviour.')}
                    </Typography>
                )}
                {(assets?.stores || []).filter((store) => !coveredStores[store.code]).map((store) => (
                    <React.Fragment key={'inh' + store.code}>
                        {fileRow(
                            <React.Fragment>
                                <Typography sx={{ fontSize: 13, color: inkFaint }}>
                                    {t('upl_store', 'Store')} {store.code} — {store.oms} · {hasGlobal ? t('upl_inherits_global', 'inherits Global') : t('upl_inherits_legacy', 'inherits the legacy file')}
                                </Typography>
                                <Box sx={{ flex: 1 }} />
                            </React.Fragment>
                        )}
                    </React.Fragment>
                ))}
                <Box sx={{ display: 'flex', justifyContent: 'center', border: '1px dashed ' + panelBorder, borderRadius: 2, p: 1.2, mt: 1 }}>
                    {smallBtn('＋ ' + t('upl_upload_scope', 'Upload for scope… (Global / Store / Register)'), () => openUploadDialog(kind), false, true)}
                </Box>
            </div>
        );
    };

    // ------------------------------------------------------------------
    //  GATES
    // ------------------------------------------------------------------

    if (!isSft) {
        return (
            <div className={containerClass}>
                <Typography sx={{ p: 4, textAlign: 'center' }}>
                    {t('upl_sft_only', 'This section is only available to SoftTouch users.')}
                </Typography>
            </div>
        );
    }

    if (loading) {
        return (
            <div className={containerClass}>
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <TailSpin height="40" width="40" color={Colors.sft_blue} ariaLabel="loading" />
                </Box>
            </div>
        );
    }

    // ------------------------------------------------------------------
    //  SCREEN
    // ------------------------------------------------------------------

    return (
        <div className={containerClass}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
                <Button size="small" sx={{ textTransform: 'none', color: Colors.sft_blue }}
                    onClick={() => navigate('/' + props.lang + '/mobile')}>
                    ‹ {t('upl_back_modules', 'Back to modules')}
                </Button>
                <Typography sx={{ fontWeight: 700, fontSize: 16 }}>MyFasMan Mobile › {t('upl_uploads', 'Uploads')}</Typography>
                <Box sx={{ flex: 1 }} />
                {/* The manifest is what the DEVICES read; regenerate publishes
                    it without needing a new upload, the link verifies it. */}
                {smallBtn(t('upl_sync_manifest', 'Sync manifest'), () => {
                    setBusy(true);
                    SftGlobalData.post('uploads&action=regenerateManifest', {
                        key: props.api, sftUserId: props.sftUserId,
                    }).then((response) => {
                        setBusy(false);
                        if (response?.success) notify(t('upl_manifest_synced', 'Manifest published'), true);
                        else notify(response?.message || 'Failed', false);
                    });
                })}
                {assets?.manifestUrl && (
                    <a href={assets.manifestUrl} target="_blank" rel="noreferrer"
                        style={{ fontSize: 13.5, color: Colors.sft_blue }}>
                        manifest.json ↗
                    </a>
                )}
            </Box>

            <Tabs value={tab} onChange={(e, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontSize: 14.5, minHeight: 44 }, minHeight: 44 }}>
                <Tab label={t('upl_tab_ticket', 'Ticket logo')} />
                <Tab label={t('upl_tab_invoice_logo', 'Invoice logo')} />
                <Tab label={t('upl_tab_documents', 'Document layouts')} />
                <Tab label={t('upl_tab_labels', 'Label layouts')} />
                <Tab label={t('upl_tab_lock_live', 'Lock screen')} />
            </Tabs>

            {tab === 0 && imageKindPane('ticket_logo',
                t('upl_ticket_intro', 'Printed on receipts and ZPL labels. The global upload also refreshes the legacy file older app versions use.'))}

            {tab === 1 && imageKindPane('invoice_logo',
                t('upl_invoice_logo_intro', 'Print order on invoices: invoice logo → client logo (licence module) → ticket logo. An empty tab still prints the client logo, like today.'))}

            {tab === 4 && imageKindPane('lock_screen',
                t('upl_lock_intro', 'The BACKGROUND image of the POS lock screen (the logo on it stays the ticket logo). Recommended: 1920×1080 px (16:9, landscape) — center-cropped to fill the screen, so portrait devices crop the sides. Max 4 MB. Without an upload the current built-in background is used, like today.'))}

            {tab === 2 && forms && (
                <div>
                    <Typography sx={{ fontWeight: 700, fontSize: 14.5, mb: 1 }}>
                        {t('upl_client_forms_title', 'Layout files of this client (custom reports)')}
                    </Typography>
                    {(forms.files || []).map((name) => (
                        <React.Fragment key={name}>
                            {fileRow(
                                <React.Fragment>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{name}</Typography>
                                    <Typography sx={{ fontSize: 12.5, color: inkFaint }}>reports2 · {t('upl_custom_dir', 'client dir')}</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {(forms.readonly_files || []).indexOf(name) >= 0 ? (
                                        // Standard (sf_) layout in the client dir: read-only.
                                        <Typography sx={{ fontSize: 12.5, color: inkFaint }}>{t('rla_readonly', 'standard layout — read-only')}</Typography>
                                    ) : (
                                        <React.Fragment>
                                            {smallBtn(t('upl_touchup_btn', 'Touch-up'), () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(name)))}
                                            {smallBtn(t('upl_delete', 'Delete'), () => deleteFr3('deleteInvoiceForm', name), true)}
                                        </React.Fragment>
                                    )}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, border: '1px dashed ' + panelBorder, borderRadius: 2, p: 1.2, my: 1, flexWrap: 'wrap' }}>
                        {smallBtn('＋ ' + t('upl_upload_fr3', 'Upload .fr3…'), () => { if (fr3FormRef.current) fr3FormRef.current.click(); }, false, true)}
                        <Typography sx={{ fontSize: 12.5, color: inkFaint }}>
                            {t('upl_frf_hint', '(.frf refused — FastReport 2 only renders on Windows MySales)')}
                        </Typography>
                    </Box>
                    <input type="file" accept=".fr3" style={{ display: "none" }} ref={fr3FormRef} onChange={(e) => uploadFr3(e, 'uploadInvoiceForm')} />

                    {[
                        { kind: 'invoice', title: t('upl_doc_invoice', 'Invoice'), kasvars: ['ReportFactuur1'] },
                        { kind: 'webprint', title: t('upl_doc_webprint', 'Webprint / delivery note'), kasvars: ['WebshopPickingListReportid', 'ReportDeliveryNote'] },
                    ].map((group) => (
                        <Box key={group.kind} sx={{ mt: 2.5 }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 15.5, mb: 0.5 }}>{group.title}</Typography>
                            {group.kasvars.filter((kasvar) => forms.documents && forms.documents[kasvar]).map((kasvar) => {
                                const doc = forms.documents[kasvar];
                                const repidKasvar = doc.accepts === 'repid';
                                return (
                                    <Box key={kasvar} sx={{ mb: 2 }}>
                                        <Typography sx={{ fontWeight: 700, fontSize: 14, mt: 1, mb: 0.3, fontFamily: 'Consolas, monospace' }}>{kasvar}</Typography>
                                        <Typography sx={{ fontSize: 12.5, color: inkFaint, mb: 0.8 }}>
                                            {kasvar === 'ReportFactuur1' && t('upl_kv_factuur', 'Which invoice report each register uses (report id, .fr3 name or TicketPrinter).')}
                                            {kasvar === 'WebshopPickingListReportid' && t('upl_kv_picking', 'Report id printed for "PRINT WEB DELIVERY NOTE" (webshop picking list); 0 = print as a plain message.')}
                                            {kasvar === 'ReportDeliveryNote' && t('upl_kv_delivery', 'Delivery note when a webshop order is processed (report id, .fr3 name or TicketPrinter).')}
                                        </Typography>
                                        <Box sx={{ overflowX: 'auto', border: '1px solid ' + panelBorder, borderRadius: 2 }}>
                                            <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 640, fontSize: 13.5 }}>
                                                <thead>
                                                    <tr>
                                                        {[t('upl_register', 'Register'), t('upl_effective', 'Effective value'), t('upl_source', 'Source'), ''].map((header, index) => (
                                                            <th key={index} style={{ textAlign: 'left', padding: '7px 10px', borderBottom: '1.5px solid ' + panelBorder, fontSize: 12, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 1 }}>{header}</th>
                                                        ))}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {(doc.assignments?.registers || []).map((register) => {
                                                        const value = (register.effective || '').trim();
                                                        const isRepid = /^\d+$/.test(value) && value !== '0';
                                                        const meta = isRepid && forms.reports ? forms.reports[value] : null;
                                                        return (
                                                            <tr key={register.code}>
                                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>{register.code} — {register.oms}</td>
                                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>
                                                                    <span style={{ fontFamily: 'Consolas, monospace' }}>{value === '' ? '—' : value}</span>
                                                                    {meta && (
                                                                        <div style={{ fontSize: 12, color: inkFaint }}>
                                                                            {(props.lang === 'fr' && meta.title2) ? meta.title2 : ((props.lang === 'en' && meta.title3) ? meta.title3 : meta.title)}
                                                                            {' · '}{meta.custom ? t('upl_custom_report', 'custom report') : t('upl_standard_report', 'standard report')}
                                                                            {meta.fastreportver === 2 ? ' · .frf' : ''}
                                                                        </div>
                                                                    )}
                                                                    {isRepid && !meta && (
                                                                        <div style={{ fontSize: 12, color: Colors.def_red }}>{t('upl_report_missing', 'report id not found in this database')}</div>
                                                                    )}
                                                                </td>
                                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>
                                                                    {register.source === 'none' ? '—' : scopeChip(register.source === 'global' ? '0000' : (register.source === 'store' ? register.code.substring(0, 2) + '00' : register.code))}
                                                                </td>
                                                                <td style={{ padding: '6px 10px', borderBottom: '1px solid ' + panelBorder }}>
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                                                                        {/\.fr3$/i.test(value) && (forms.files || []).indexOf(value) >= 0
                                                                            && (forms.readonly_files || []).indexOf(value) < 0
                                                                            && smallBtn(t('upl_touchup_btn', 'Touch-up'),
                                                                                () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(value)))}
                                                                        {isRepid && meta && !meta.custom && smallBtn(t('rla_copy_custom', 'Copy as custom report'),
                                                                            () => copyReportAsCustom(value, meta.title, kasvar,
                                                                                register.source === 'store' ? register.code.substring(0, 2) + '00'
                                                                                    : (register.source === 'register' ? register.code : '0000')))}
                                                                        {isRepid && meta && meta.custom && ((forms.repid_forms ? forms.repid_forms[value] : null) || []).filter((entry) => !entry.lang && entry.editable !== false).map((entry) => (
                                                                            <React.Fragment key={entry.name}>
                                                                                {entry.present
                                                                                    ? smallBtn(t('upl_touchup_btn', 'Touch-up'),
                                                                                        () => navigate('/' + props.lang + '/mobile/designer/reports/' + encodeURIComponent(entry.name)))
                                                                                    : smallBtn(t('upl_fetch_layout', 'Fetch layout'),
                                                                                        () => importReportForm(entry.name))}
                                                                            </React.Fragment>
                                                                        ))}
                                                                        {isRepid && meta && smallBtn(t('upl_preview_doc', 'Preview with document…'), () => setPreview({
                                                                            repid: parseInt(value, 10),
                                                                            title: meta.title || ('#' + value),
                                                                            kind: doc.kind === 'invoice' ? 'invoice' : 'ticket',
                                                                            storeId: register.code.substring(0, 2),
                                                                            posId: register.code,
                                                                        }))}
                                                                        {smallBtn(t('upl_change_value', 'Change…'), () => {
                                                                            // Prefill the assignment dialog at the
                                                                            // scope the effective value comes from.
                                                                            setAsgKasvar(kasvar);
                                                                            if (register.source === 'store') {
                                                                                setAsgScopeType('store'); setAsgStore(register.code.substring(0, 2)); setAsgRegister('');
                                                                            } else if (register.source === 'register') {
                                                                                setAsgScopeType('register'); setAsgStore(''); setAsgRegister(register.code);
                                                                            } else {
                                                                                setAsgScopeType('global'); setAsgStore(''); setAsgRegister('');
                                                                            }
                                                                            setAsgValue(register.effective || '');
                                                                            setAsgOpen(true);
                                                                        })}
                                                                        {register.source === 'register' && smallBtn(t('upl_clear_override', 'Clear override'), () => clearAssignment(kasvar, register.code), true)}
                                                                    </Box>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>
                                        <Box sx={{ mt: 1 }}>
                                            {smallBtn(t('upl_set_assignment', 'Set assignment…'), () => {
                                                setAsgKasvar(kasvar);
                                                setAsgScopeType('global'); setAsgStore(''); setAsgRegister('');
                                                setAsgValue(repidKasvar ? '0' : '');
                                                setAsgOpen(true);
                                            }, false, true)}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    ))}
                </div>
            )}

            {tab === 3 && (
                <div>
                    {fileRow(
                        <React.Fragment>
                            <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('upl_classic_zpl', 'Classic ZPL layout')}</Typography>
                            <Typography sx={{ fontSize: 12.5, color: inkFaint }}>
                                kasvar mobileZPLcode · {t('upl_classic_hint', 'used unless a device selects an .fr3 below')}
                            </Typography>
                            <Box sx={{ flex: 1 }} />
                            {smallBtn(t('upl_open_designer', 'Open designer'), () => navigate('/' + props.lang + '/mobile/designer'), false, true)}
                        </React.Fragment>
                    )}
                    {(assets?.labels || []).map((file) => (
                        <React.Fragment key={file.name}>
                            {fileRow(
                                <React.Fragment>
                                    <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{file.name}</Typography>
                                    <Box sx={{ flex: 1 }} />
                                    {smallBtn(t('upl_edit', 'Edit'), () => navigate('/' + props.lang + '/mobile/designer/labels/' + encodeURIComponent(file.name)))}
                                    {smallBtn(t('upl_delete', 'Delete'), () => deleteFr3('deleteLabelLayout', file.name), true)}
                                </React.Fragment>
                            )}
                        </React.Fragment>
                    ))}
                    <Box sx={{ display: 'flex', justifyContent: 'center', border: '1px dashed ' + panelBorder, borderRadius: 2, p: 1.2, mt: 1 }}>
                        {smallBtn('＋ ' + t('upl_upload_label', 'Upload .fr3 label layout…'), () => { if (fr3LabelRef.current) fr3LabelRef.current.click(); }, false, true)}
                    </Box>
                    <input type="file" accept=".fr3" style={{ display: "none" }} ref={fr3LabelRef} onChange={(e) => uploadFr3(e, 'uploadLabelLayout')} />
                </div>
            )}

            {/* Image upload dialog */}
            <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>{t('upl_upload_title', 'Upload image')}</DialogTitle>
                <DialogContent>
                    <Select fullWidth size="small" value={dlgScopeType} sx={{ mb: 1.2, fontSize: 14 }}
                        onChange={(e) => setDlgScopeType(e.target.value)}>
                        <MenuItem value="global" sx={{ fontSize: 14 }}>{t('upl_global', 'Global')}</MenuItem>
                        <MenuItem value="store" sx={{ fontSize: 14 }}>{t('upl_store', 'Store')}</MenuItem>
                        <MenuItem value="register" sx={{ fontSize: 14 }}>{t('upl_register', 'Register')}</MenuItem>
                    </Select>
                    {dlgScopeType === 'store' && (
                        <Select fullWidth size="small" value={dlgStore} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setDlgStore(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_store', 'Choose a store…')}</MenuItem>
                            {(assets?.stores || []).map((store) => (
                                <MenuItem key={store.code} value={store.code} sx={{ fontSize: 14 }}>{store.code} — {store.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {dlgScopeType === 'register' && (
                        <Select fullWidth size="small" value={dlgRegister} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setDlgRegister(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_register', 'Choose a register…')}</MenuItem>
                            {(assets?.registers || []).map((register) => (
                                <MenuItem key={register.code} value={register.code} sx={{ fontSize: 14 }}>{register.code} — {register.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {dlgImage !== '' && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1.2 }}>
                            <img src={dlgImage} alt="preview" style={{ maxWidth: 260, maxHeight: 110, objectFit: 'contain', border: '1px solid #C9D2DA', borderRadius: 4 }} />
                        </Box>
                    )}
                    <Button fullWidth variant="outlined" size="small" sx={{ textTransform: 'none', fontSize: 13 }}
                        onClick={() => { if (imgFileRef.current) imgFileRef.current.click(); }}>
                        {t('upl_pick_image', 'Choose image (PNG / JPEG / BMP)…')}
                    </Button>
                    <input type="file" accept="image/png,image/jpeg,image/bmp" style={{ display: "none" }} ref={imgFileRef} onChange={pickImage} />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDlgOpen(false)} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button disabled={busy || dlgImage === '' || dialogScope(dlgScopeType, dlgStore, dlgRegister) === ''}
                        variant="contained" onClick={submitUpload}
                        sx={{ textTransform: 'none', backgroundColor: Colors.sft_blue }}>
                        {t('upl_upload', 'Upload')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Assignment dialog */}
            <Dialog open={asgOpen} onClose={() => setAsgOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15 }}>{t('upl_set_assignment', 'Set assignment…')} — <span style={{ fontFamily: 'Consolas, monospace' }}>{asgKasvar}</span></DialogTitle>
                <DialogContent>
                    <Select fullWidth size="small" value={asgScopeType} sx={{ mb: 1.2, fontSize: 14 }}
                        onChange={(e) => setAsgScopeType(e.target.value)}>
                        <MenuItem value="global" sx={{ fontSize: 14 }}>{t('upl_global', 'Global')}</MenuItem>
                        <MenuItem value="store" sx={{ fontSize: 14 }}>{t('upl_store', 'Store')}</MenuItem>
                        <MenuItem value="register" sx={{ fontSize: 14 }}>{t('upl_register', 'Register')}</MenuItem>
                    </Select>
                    {asgScopeType === 'store' && (
                        <Select fullWidth size="small" value={asgStore} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setAsgStore(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_store', 'Choose a store…')}</MenuItem>
                            {(assets?.stores || []).map((store) => (
                                <MenuItem key={store.code} value={store.code} sx={{ fontSize: 14 }}>{store.code} — {store.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {asgScopeType === 'register' && (
                        <Select fullWidth size="small" value={asgRegister} displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                            onChange={(e) => setAsgRegister(e.target.value)}>
                            <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_register', 'Choose a register…')}</MenuItem>
                            {(assets?.registers || []).map((register) => (
                                <MenuItem key={register.code} value={register.code} sx={{ fontSize: 14 }}>{register.code} — {register.oms}</MenuItem>
                            ))}
                        </Select>
                    )}
                    {forms?.documents && forms.documents[asgKasvar] && forms.documents[asgKasvar].accepts === 'repid' ? (
                        <TextField
                            fullWidth size="small" value={asgValue}
                            onChange={(e) => setAsgValue(e.target.value.replace(/[^0-9]/g, ''))}
                            label={t('upl_value_repid', 'Report id (0 = off)')}
                            inputProps={{ style: { fontSize: 14 }, inputMode: 'numeric' }}
                        />
                    ) : (
                        <React.Fragment>
                            <Select fullWidth size="small" displayEmpty sx={{ mb: 1.2, fontSize: 14 }}
                                value={(forms?.files || []).indexOf(asgValue) >= 0 || asgValue === 'TicketPrinter' ? asgValue : ''}
                                onChange={(e) => setAsgValue(e.target.value)}>
                                <MenuItem value="" disabled sx={{ fontSize: 14 }}>{t('upl_pick_form', 'Choose a form / option…')}</MenuItem>
                                <MenuItem value="TicketPrinter" sx={{ fontSize: 14 }}>TicketPrinter ({t('upl_ticketprinter_hint', 'print on the receipt printer')})</MenuItem>
                                {(forms?.files || []).map((name) => (
                                    <MenuItem key={name} value={name} sx={{ fontSize: 14 }}>{name}</MenuItem>
                                ))}
                            </Select>
                            <TextField
                                fullWidth size="small" value={asgValue}
                                onChange={(e) => setAsgValue(e.target.value)}
                                label={t('upl_value_free', 'Or type a value (report id / .fr3 name)')}
                                inputProps={{ style: { fontSize: 14 } }}
                            />
                        </React.Fragment>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setAsgOpen(false)} sx={{ textTransform: 'none' }}>{t('cancel', 'Cancel')}</Button>
                    <Button disabled={busy || dialogScope(asgScopeType, asgStore, asgRegister) === ''}
                        variant="contained" onClick={submitAssignment}
                        sx={{ textTransform: 'none', backgroundColor: Colors.sft_blue }}>
                        {t('save', 'Save')}
                    </Button>
                </DialogActions>
            </Dialog>

            <ReportPreviewDialog
                open={preview !== null}
                onClose={() => setPreview(null)}
                api={props.api}
                sftUserId={props.sftUserId}
                t={props.t}
                lang={props.lang}
                theme={props.theme}
                repid={preview?.repid}
                title={preview?.title}
                documentKind={preview?.kind}
                storeId={preview?.storeId}
                posId={preview?.posId}
                recIdSlots={preview?.kind === 'invoice'
                    ? [{ slot: 1, label: t('upl_recid_docnr', 'Invoice number (docnr)') }, { slot: 2, label: t('upl_recid_coid', 'Company (coid)') }]
                    : [{ slot: 1, label: t('upl_recid_ticketnr', 'Ticket number') }]}
            />
        </div>
    );
};

export default UploadsScreen;
